import type { Store } from 'redux';
import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { metrics } from '@atlassian/browser-metrics';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fetchProformaReqTypeConfig } from '@atlassian/jira-proforma-request-type-config/src/services/fetch-proforma-rtc/index.tsx';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger.tsx';
import type {
	SaveChangesServiceParams,
	UpdateIssueLayoutApiResponse,
	SaveChangesServiceResponse,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/save-changes.tsx';
import type { UpdateIssueTypeRequest } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/index.tsx';
import {
	fetchProformaReqTypeConfigSuccess,
	removeProformaReqTypeFormSuccess,
	saveChangesStart,
	saveChangesSuccess,
} from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/utils/proforma/state/actions/index.tsx';
import {
	getCloudId,
	proformaReqTypeFormAddRequest,
	proformaReqTypeFormRemovalRequest,
} from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/utils/proforma/state/selectors.tsx';
import { transformUpdateLayoutResponse } from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/utils/transformers.tsx';
import { addProformaReqTypeForm } from '@atlassian/jira-project-settings-product-features-jsm-common/src/services/add-proforma-req-type-form/index.tsx';
import { removeProformaReqTypeForm } from '@atlassian/jira-project-settings-product-features-jsm-common/src/services/remove-proforma-req-type-form/index.tsx';
import { PACKAGE_NAME } from '../../constants.tsx';
import { transformRequest } from './transformer/index.tsx';

const updateIssueTypeTimer = metrics.custom({
	key: 'project-settings.issue-type.jsw-tmp.update',
});

// Endpoint response is the same shape as the request data
// https://hello.atlassian.net/wiki/spaces/JPCE/pages/1272246008/LDR%3A+SWIFT+APIs+data+shape+for+Issue+Layout+data+model?focusedCommentId=1315310454#comment-1315310454
export const updateIssueType = (
	projectId: number,
	layoutId: string,
	extraDefinerId: number,
	{ issueLayout, owners }: UpdateIssueTypeRequest,
): Promise<UpdateIssueLayoutApiResponse> =>
	performPutRequest(`/rest/internal/1.0/issueLayouts/${layoutId}`, {
		body: JSON.stringify({
			projectId,
			extraDefinerId,
			owners,
			issueLayoutType: 'ISSUE_VIEW',
			issueLayoutConfig: issueLayout,
		}),
	});

export const saveChangesService = async ({
	projectId,
	layoutId,
	extraDefinerId,
	data,
}: SaveChangesServiceParams): Promise<SaveChangesServiceResponse> => {
	const transformedData: UpdateIssueTypeRequest = transformRequest(data);

	updateIssueTypeTimer.start();

	try {
		const response = await updateIssueType(projectId, layoutId, extraDefinerId, transformedData);

		const result: UpdateIssueLayoutApiResponse = {
			...response,
		};

		updateIssueTypeTimer.stop();
		return transformUpdateLayoutResponse(result);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logError(
			PACKAGE_NAME,
			toErrorId('updateIssueLayoutJsmTeamManaged'),
			toErrorMessage(
				error.errors?.map((e: { error: string }) => e.error)?.toString() ||
					'The request failed trying to update issue layout',
			),
			error,
		);

		updateIssueTypeTimer.stop();
		throw error;
	}
};

export const saveChangesServiceWithProformaContext =
	(store: Store, createAnalyticsEvent?: (payload: AnalyticsEventPayload) => UIAnalyticsEvent) =>
	async ({
		projectId,
		layoutId,
		extraDefinerId,
		data,
	}: SaveChangesServiceParams): Promise<SaveChangesServiceResponse> => {
		const state = store.getState();
		const cloudId = getCloudId(state);

		const transformedData: UpdateIssueTypeRequest = transformRequest(data);

		updateIssueTypeTimer.start();

		try {
			store.dispatch(saveChangesStart());
			const response = await updateIssueType(projectId, layoutId, extraDefinerId, transformedData);

			// Call remove and add in sequence so that ProformaForm is added
			// ONLY WHEN remove previous form succeed
			await removeProformaReqTypeForm(
				proformaReqTypeFormRemovalRequest(state),
				createAnalyticsEvent,
			);
			await addProformaReqTypeForm(proformaReqTypeFormAddRequest(state), createAnalyticsEvent);

			const result: UpdateIssueLayoutApiResponse = {
				...response,
			};

			updateIssueTypeTimer.stop();

			// get the latest backend state
			await fetchProformaReqTypeConfig({
				cloudId,
				projectId,
				requestTypeId: extraDefinerId,
			}).then((config) => {
				if (config !== null) {
					store.dispatch(fetchProformaReqTypeConfigSuccess(config));
				} else {
					store.dispatch(removeProformaReqTypeFormSuccess());
				}
			});

			store.dispatch(saveChangesSuccess());
			return transformUpdateLayoutResponse(result);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			logError(
				PACKAGE_NAME,
				toErrorId('updateIssueLayoutJsmTeamManaged'),
				toErrorMessage(
					error.errors?.map((e: { error: string }) => e.error)?.toString() ||
						'The request failed trying to update issue layout',
				),
				error,
			);

			updateIssueTypeTimer.stop();
			throw error;
		}
	};
